import React, { useContext } from "react";
import {
  AppBar,
  Badge,
  Box,
  IconButton,
  Toolbar,
  Typography,
  useScrollTrigger,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import SettingsIcon from "@mui/icons-material/Settings";
import NotificationMenu from "./NotificationMenu";
import SettingsMenu from "./SettingsMenu";
import { ThemeContext } from "../../utils/ThemeContext";

const TopBar = ({
  pageTitle,
  isFullscreen,
  toggleFullscreen,
  hasPermission,
  notifications,
  notificationsAnchorEl,
  openNotificationsMenu,
  closeNotificationsMenu,
  clearNotifications,
  unreadCount,
  navigate,
  settingsAnchorEl,
  openSettingsMenu,
  closeSettingsMenu,
  handleUserProfile,
  handleSystemPreferences,
  handleThemeSettings,
}) => {
  const { theme } = useContext(ThemeContext);

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 10,
  });

  const defaultAppBarColor = "#2f63ad";
  const appBarColor = theme?.appBarColor || defaultAppBarColor;

  const borderColor = "rgba(255,255,255,0.14)";
  const iconBg = trigger
    ? "rgba(255,255,255,0.18)"
    : "rgba(255,255,255,0.15)";
  const iconHoverBg = trigger
    ? "rgba(255,255,255,0.28)"
    : "rgba(255,255,255,0.25)";

  const iconButtonSx = {
    color: "#fff",
    backgroundColor: iconBg,
    border: `1px solid ${borderColor}`,
    width: 30,
    height: 30,
    transition: "all 0.2s ease",
    "& svg": {
      fontSize: 18,
    },
    "&:hover": {
      backgroundColor: iconHoverBg,
    },
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: trigger ? `${appBarColor}CC` : appBarColor,
        boxShadow: trigger
          ? "0 6px 16px rgba(0,0,0,0.12)"
          : "0 6px 16px rgba(0,0,0,0.14)",
        borderBottom: `1px solid ${borderColor}`,
        px: { xs: 1, sm: 1.5, md: 2 },
        py: 0.5,
        zIndex: 1100,
        backdropFilter: trigger ? "blur(10px) saturate(140%)" : "none",
        WebkitBackdropFilter: trigger ? "blur(10px) saturate(140%)" : "none",
        transition: "all 0.3s ease",
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: "40px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          pl: { xs: 1.5, sm: 2, md: 2.5 },
          pr: 0,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: "#f4f7fb",
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            letterSpacing: "0.2px",
            fontSize: "0.95rem",
            lineHeight: 1.2,
          }}
        >
          {pageTitle}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          {hasPermission("view_notifications") && (
            <>
              <IconButton onClick={openNotificationsMenu} sx={iconButtonSx}>
                <Badge
                  badgeContent={unreadCount}
                  max={99}
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "#ff4d4f",
                      color: "#fff",
                      fontSize: "0.6rem",
                      fontWeight: 700,
                      minWidth: 16,
                      height: 16,
                      padding: "0 3px",
                      border: `1.5px solid ${appBarColor}`,
                    },
                  }}
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>

              <NotificationMenu
                anchorEl={notificationsAnchorEl}
                notifications={notifications}
                onClose={closeNotificationsMenu}
                onClear={clearNotifications}
                navigate={navigate}
              />
            </>
          )}

          <IconButton onClick={toggleFullscreen} sx={iconButtonSx}>
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>

          {hasPermission("manage_settings") && (
            <>
              <IconButton onClick={openSettingsMenu} sx={iconButtonSx}>
                <SettingsIcon />
              </IconButton>

              <SettingsMenu
                anchorEl={settingsAnchorEl}
                onClose={closeSettingsMenu}
                onUserProfile={handleUserProfile}
                onSystemPreferences={handleSystemPreferences}
                onThemeSettings={handleThemeSettings}
                hasPermission={hasPermission}
              />
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;